/**
 * Клиентский стриминг ассистента.
 *
 * Сначала пробуем SSE-эндпоинт: текст приходит кусками, и пользователь видит
 * ответ через 1–3 секунды вместо 10–23. Если стрим оборвался или провайдер
 * отдал ошибку — откатываемся на обычный POST, чтобы ответ всё равно был.
 */

export interface StreamAssistantInput {
  scene: string;
  slug?: string;
  message?: string;
  history?: Array<{ role: "user" | "assistant"; content: string }>;
  locale?: string;
  page?: string;
  pageText?: string;
  sessionId?: string;
}

export interface StreamAssistantResult {
  reply: string;
  /** Ответ собран из стрима (true) или получен откатом на POST (false). */
  streamed: boolean;
}

interface StreamEvent {
  type: "delta" | "done" | "error";
  text?: string;
  message?: string;
}

function parseSseLine(line: string): StreamEvent | null {
  if (!line.startsWith("data:")) return null;
  const payload = line.slice(5).trim();
  if (!payload) return null;
  try {
    return JSON.parse(payload) as StreamEvent;
  } catch {
    return null;
  }
}

async function readSse(
  response: Response,
  onChunk: (text: string) => void
): Promise<{ ok: boolean; error?: string }> {
  const reader = response.body?.getReader();
  if (!reader) return { ok: false, error: "Нет тела ответа" };

  const decoder = new TextDecoder();
  let buffer = "";

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const events = buffer.split(/\r?\n\r?\n/);
      buffer = events.pop() ?? "";

      for (const event of events) {
        for (const line of event.split(/\r?\n/)) {
          const parsed = parseSseLine(line);
          if (!parsed) continue;
          if (parsed.type === "delta" && parsed.text) onChunk(parsed.text);
          if (parsed.type === "error") return { ok: false, error: parsed.message };
        }
      }
    }

    for (const line of buffer.split(/\r?\n/)) {
      const parsed = parseSseLine(line);
      if (!parsed) continue;
      if (parsed.type === "delta" && parsed.text) onChunk(parsed.text);
      if (parsed.type === "error") return { ok: false, error: parsed.message };
    }

    return { ok: true };
  } finally {
    reader.releaseLock();
  }
}

/**
 * Спросить ассистента со стримингом. onChunk вызывается на каждый кусок ответа.
 * При любой ошибке стрима откатывается на обычный POST и возвращает полный ответ.
 */
export async function streamAssistant(
  input: StreamAssistantInput,
  onChunk: (text: string) => void
): Promise<StreamAssistantResult> {
  const body = {
    scene: input.scene,
    slug: input.slug,
    message: input.message,
    history: input.history,
    locale: input.locale,
    page: input.page,
    pageText: input.pageText,
    sessionId: input.sessionId,
  };

  let response: Response;
  try {
    response = await fetch("/api/assistant/stream", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    return fallbackPost(body, onChunk);
  }

  if (!response.ok) {
    return fallbackPost(body, onChunk);
  }

  const collected: string[] = [];
  const result = await readSse(response, (text) => {
    collected.push(text);
    onChunk(text);
  });

  if (result.ok && collected.length > 0) {
    return { reply: collected.join(""), streamed: true };
  }

  return fallbackPost(body, onChunk);
}

async function fallbackPost(
  body: Record<string, unknown>,
  onChunk: (text: string) => void
): Promise<StreamAssistantResult> {
  const response = await fetch("/api/assistant", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const data = (await response.json()) as { reply?: string; error?: string };
  if (!response.ok || !data.reply) {
    throw new Error(data.error || "Не удалось получить ответ");
  }

  onChunk(data.reply);
  return { reply: data.reply, streamed: false };
}
